import './pagination.scss';

import template from './pagination.html';

import Events from '@/scripts/lib/Events';
import microTemplate from '@/scripts/lib/microTmpl';

import breakpoints from '@/scripts/common/breakpoints';
import paginate from './algorithms/paginate';

/**
 * @typedef {{
 *    delta?: number,
 *    linkGenerator?: (page: number) => string,
 *    manualRouting: boolean,
 * }} PaginationOptions
 */

/** @type {PaginationOptions} */
const DEF_OPTIONS = {
   // The number of visible page buttons at both sides of cur page
   delta: 2,
   manualRouting: true,
   linkGenerator: defaultLinkGenerator,
};

export default class Pagination {
   /**
    * @param {HTMLElement} root 
    * @param {PaginationOptions} options
    */
   constructor(root, options) {
      this.events = new Events();
      this._paginate = paginate;

      this._options = { ...DEF_OPTIONS, ...options };
      this._root = root;

      this._pages = 10;
      this._cur = 1;

      this._pageNumberButtons = [];

      this._disabled = false;

      this._mobileMedia = matchMedia(`
         screen and (max-width: ${breakpoints.largeMobile}px)
      `);

      this._initEvents();
   }

   render() {
      this._updateConfig();
      this._root.innerHTML = microTemplate(template, this);
   }

   _initEvents() {
      if (!this._options.manualRouting) return;
         
      this._root.addEventListener('click', (event) => {
         const selector = '.pagination--link:not(.pagination--disabled)';
         const pageBtn = event.target.closest(selector);

         if (pageBtn) {
            event.preventDefault();

            if (this._disabled) return;

            this._setPage(+pageBtn.dataset.page);
            return;
         }
      });

      this._mobileMedia.addListener(() => {
         this.render();
      });
   }

   _setPage(newPage) {
      newPage = parseInt(newPage + '');

      if (isNaN(newPage) || newPage === this._cur) return;
      const oldPage = this._cur;
      this._cur = newPage;

      this.render();
      this.events.emit('change', newPage, oldPage);
   }

   _updateConfig() {
      this._pageNumberButtons = this._paginate({
         cur: this._cur,
         pages: this._pages,
         delta: this._delta,
      });
   }

   get _delta() {
      if (this._mobileMedia.matches) return 1;
      return this._options.delta;
   }

   get cur() { return this._cur; }
   set cur(val) { this._cur = val; }

   get pages() { return this._pages; }
   set pages(val) { this._pages = val; }

   get disabled() { return this._disabled; }
   set disabled(val) { this._disabled = val; }
}

/**
 * @param {number} page 
 * @returns {string}
 */
function defaultLinkGenerator(page) {
   const url = new URL(location);
   url.searchParams.set('page', page);
   if (page <= 1) url.searchParams.delete('page');

   return url;
}